import React, { useEffect, useState } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import TablePagination from '../../components/TablePagination';
import ProjectNameText from '../../components/ProjectNameText';
import TabbedSection from '../../components/TabbedSection';
import { useProjects } from '../../context/ProjectsContext';
import { submittalsAPI } from '../../services/authService';
import { formatDateIN } from '../../utils/dateFormat';
import SubmittalStatusBadge from './SubmittalStatusBadge';

const INBOX_TABS = [
  { key: 'my-pending', label: 'My Pending' },
  { key: 'returned', label: 'Returned' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'pending-consultant', label: 'Pending Consultant' },
  { key: 'pending-client', label: 'Pending Client' },
];

const ApprovalInbox = ({ onViewSubmittal }) => {
  const { projects } = useProjects();
  const [activeTab, setActiveTab] = useState('my-pending');
  const [projectFilter, setProjectFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const perPage = 50;

  useEffect(() => {
    setPage(1);
  }, [activeTab, projectFilter]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = { page, pageSize: perPage };
        if (projectFilter !== 'all') params.projectId = projectFilter;
        let resp;
        if (activeTab === 'my-pending') resp = await submittalsAPI.getMyPending(params);
        else if (activeTab === 'returned') resp = await submittalsAPI.getReturned(params);
        else if (activeTab === 'overdue') resp = await submittalsAPI.getOverdue(params);
        else if (activeTab === 'pending-consultant') resp = await submittalsAPI.getAll({ ...params, pendingPartyType: 'CONSULTANT' });
        else resp = await submittalsAPI.getAll({ ...params, pendingPartyType: 'CLIENT' });
        const { rows: dataRows, totalCount: cnt } = unwrapListResponse(resp);
        if (!cancelled) {
          setRows(safeArray(dataRows));
          setTotalCount(cnt);
        }
      } catch (err) {
        console.warn('Inbox load failed:', err?.message || err);
        if (!cancelled) { setRows([]); setTotalCount(0); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [activeTab, page, projectFilter]);

  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));

  const table = (
    <>
      <div className="filters-row" style={{ marginBottom: 12 }}>
        <ProjectFilterSelect value={projectFilter} onChange={setProjectFilter} projects={projects} />
      </div>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Submittal No.</th>
              <th>Project</th>
              <th>Subject</th>
              <th>Status</th>
              <th>Pending With</th>
              <th>Due Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center' }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#6b7280' }}>No items in this inbox</td></tr>
            ) : rows.map((row) => (
              <tr
                key={row.id}
                style={{ cursor: typeof onViewSubmittal === 'function' ? 'pointer' : 'default' }}
                onClick={() => onViewSubmittal?.(row.id)}
              >
                <td style={{ fontWeight: 600, color: '#7C3AED' }}>{row.submittal_no || `#${row.id}`}</td>
                <td><ProjectNameText as="span">{row.project?.name || '—'}</ProjectNameText></td>
                <td>{row.subject}</td>
                <td><SubmittalStatusBadge status={row.status} isOverdue={row.is_overdue} /></td>
                <td>{row.pending_party?.name || row.current_pending_party_type || '—'}</td>
                <td>{formatDateIN(row.due_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePagination page={page} totalPages={totalPages} totalCount={totalCount} onPageChange={setPage} />
    </>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Approval Inbox</h2>
          <p style={{ color: '#6b7280', margin: 0 }}>Pending reviews, returns, and overdue submittals requiring action.</p>
        </div>
      </div>

      <TabbedSection
        activeKey={activeTab}
        onChange={setActiveTab}
        tabs={INBOX_TABS.map((t) => ({ ...t, content: table }))}
      />
    </div>
  );
};

export default ApprovalInbox;
